import { calcRSI } from "./rsiScoring.js";

function sma(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  return closes.slice(0, period).reduce((s, v) => s + v, 0) / period;
}

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function donchianUpper(highs, n = 20) {
  if (!highs?.length || highs.length < n + 1) return null;
  return Math.max(...highs.slice(1, n + 1));
}

function avgVolOnDownUp(closes, volumes, downSessions = 5, upLookback = 15) {
  if (!closes?.length || !volumes?.length) return { downAvg: null, upAvg: null };
  let downVol = 0;
  let downN = 0;
  for (let i = 0; i < downSessions && i + 1 < closes.length; i++) {
    if (closes[i] < closes[i + 1]) {
      downVol += volumes[i] || 0;
      downN += 1;
    }
  }
  let upVol = 0;
  let upN = 0;
  for (let i = downSessions; i < downSessions + upLookback && i + 1 < closes.length; i++) {
    if (closes[i] > closes[i + 1]) {
      upVol += volumes[i] || 0;
      upN += 1;
    }
  }
  return {
    downAvg: downN ? downVol / downN : null,
    upAvg: upN ? upVol / upN : null,
  };
}

function hammerAtSupport(opens, highs, lows, closes) {
  if (!opens?.length || !highs?.length || !lows?.length || !closes?.length) return false;
  const o = opens[0];
  const h = highs[0];
  const l = lows[0];
  const c = closes[0];
  if ([o, h, l, c].some((x) => x == null)) return false;
  const body = Math.abs(c - o);
  const lower = Math.min(o, c) - l;
  const upper = h - Math.max(o, c);
  return body > 0 && lower >= body * 2 && upper <= body * 0.5;
}

function rsi2CrossAbove10(closes) {
  const r0 = calcRSI(closes, 2);
  const r1 = closes.length > 3 ? calcRSI(closes.slice(1), 2) : null;
  return r0 != null && r1 != null && r1 < 10 && r0 >= 10;
}

function nearLevel(price, level, pct = 0.02) {
  if (price == null || level == null || level <= 0) return false;
  return Math.abs(price - level) / level <= pct;
}

function declineFromHigh(closes, window = 20) {
  if (!closes?.length) return null;
  const slice = closes.slice(0, Math.min(window, closes.length));
  const hi = Math.max(...slice);
  if (!hi) return null;
  return ((hi - closes[0]) / hi) * 100;
}

function spyDeclineFromHigh(spyCloses, window = 20) {
  return declineFromHigh(spyCloses, window);
}

/**
 * @returns {object} dip checklist payload
 */
export function evaluateDipSetup(result, marketCtx = {}) {
  const passed = [];
  const failed = [];
  const closes = result?.closes;
  const highs = result?.highs;
  const lows = result?.lows;
  const volumes = result?.volumes;
  const opens = result?.opens;
  const price = result?.price ?? closes?.[0];
  const symbol = result?.symbol ?? "";

  const empty = {
    status: "NONE",
    passed,
    failed,
    reason: "",
    rsDuringDrop: null,
    supportLevel: null,
    stop: null,
    t1: null,
    t2: null,
    rr1: null,
    actionable: false,
  };

  if (!closes?.length || price == null) return empty;

  const ema50 = result.sm50 ?? result.ema50 ?? emaLatest(closes, 50);
  const ema200 = result.sm200 ?? result.ema200 ?? emaLatest(closes, 200);
  const ema20 = result.ema21 ?? emaLatest(closes, 20);
  const trendOk =
    ema200 != null && price > ema200 && ema50 != null && ema50 > ema200;
  if (trendOk) passed.push("Trend intact");
  else failed.push({ id: "trend", shortLabel: "Trend not intact" });

  const high20 = Math.max(...closes.slice(0, Math.min(20, closes.length)));
  const pullPct = high20 > 0 ? ((high20 - price) / high20) * 100 : null;
  const pullOk = pullPct != null && pullPct >= 3 && pullPct <= 12;
  if (pullOk) passed.push(`Pullback ${pullPct.toFixed(1)}%`);
  else failed.push({ id: "pullback", shortLabel: "Not in 3–12% pullback zone" });

  const donchUpper = donchianUpper(highs);
  let supportLevel = null;
  if (nearLevel(price, ema20)) supportLevel = ema20;
  else if (nearLevel(price, ema50)) supportLevel = ema50;
  else if (donchUpper != null && nearLevel(price, donchUpper)) supportLevel = donchUpper;

  let reclaimedEma50 = false;
  if (ema50 != null && closes.length >= 3) {
    const wasBelow = closes[1] < ema50 || closes[2] < ema50;
    reclaimedEma50 = wasBelow && price >= ema50;
  }
  const atSupport = supportLevel != null || reclaimedEma50;
  if (atSupport) passed.push(reclaimedEma50 ? "Reclaimed EMA50" : "At support");
  else failed.push({ id: "support", shortLabel: "Not at support" });

  const rsi14 = result.rsi ?? calcRSI(closes, 14);
  const rsi2 = calcRSI(closes, 2);
  const oversoldOk = (rsi14 != null && rsi14 >= 35 && rsi14 <= 50) || (rsi2 != null && rsi2 < 10);
  if (oversoldOk) passed.push("Oversold short-term");
  else failed.push({ id: "rsi", shortLabel: "RSI not in dip zone" });

  const stockDrop = declineFromHigh(closes, 20);
  const spyCloses = marketCtx.spyCloses ?? result.phaseContext?.spyCloses;
  const spyDrop = spyDeclineFromHigh(spyCloses, 20);
  let rsDuringDrop = null;
  if (stockDrop != null && spyDrop != null) {
    rsDuringDrop = spyDrop - stockDrop;
  }
  const rsOk = rsDuringDrop != null && rsDuringDrop > 0;
  if (rsOk) passed.push("Holding up vs SPY");
  else failed.push({ id: "rs", shortLabel: "Weaker than SPY on pullback" });

  const { downAvg, upAvg } = avgVolOnDownUp(closes, volumes);
  const lightSell =
    downAvg != null && upAvg != null && downAvg < upAvg && (result.cmf ?? 0) > -0.1;
  if (lightSell) passed.push("Light selling");
  else failed.push({ id: "volume", shortLabel: "Heavy distribution volume" });

  const setupReady = failed.length === 0;

  const turnToday =
    (highs?.length > 1 && closes[0] > highs[1]) ||
    hammerAtSupport(opens, highs, lows, closes) ||
    rsi2CrossAbove10(closes);

  let status = "NONE";
  if (setupReady && turnToday) status = "TRIGGERED";
  else if (setupReady) status = "SETUP";

  const spyAbove200 = marketCtx.spyAbove200 !== false;
  let reason = "";
  let actionable = status === "TRIGGERED";

  if (!spyAbove200 && status !== "NONE") {
    if (rsDuringDrop == null || rsDuringDrop <= 3) {
      if (status === "TRIGGERED") {
        status = "SETUP";
        actionable = false;
        reason = "Bear market — higher risk; need RS vs SPY > 3pp (turn not actionable yet)";
      } else {
        reason = "Bear market — higher risk; only TRIGGERED entries when RS > 3pp";
      }
    } else if (status === "SETUP") {
      reason = "Bear market — higher risk; wait for turn trigger";
      actionable = false;
    } else {
      reason = "Bear market — higher risk";
    }
  }

  if (status === "TRIGGERED" && spyAbove200 === false && rsDuringDrop != null && rsDuringDrop > 3) {
    actionable = true;
  } else if (status === "TRIGGERED" && spyAbove200 !== false) {
    actionable = true;
  } else if (status === "TRIGGERED" && !spyAbove200) {
    actionable = rsDuringDrop != null && rsDuringDrop > 3;
    if (!actionable) status = "SETUP";
  }

  let stop = null;
  let t1 = high20;
  let t2 = null;
  let rr1 = null;
  if (supportLevel != null && result.atr != null) {
    stop = supportLevel - 0.5 * result.atr;
    t2 = t1 != null ? t1 + result.atr : null;
    if (stop != null && t1 != null && price > stop) {
      rr1 = (t1 - price) / (price - stop);
    }
  }

  if (status === "TRIGGERED" && rr1 != null && rr1 < 1.5) {
    status = "SETUP";
    actionable = false;
    reason = reason
      ? `${reason}; Reward:risk too low`
      : "Reward:risk too low";
  }

  if (status === "SETUP" && !reason && failed.length) {
    reason = failed[0]?.shortLabel ?? "Setup building";
  }

  return {
    status,
    passed,
    failed,
    reason,
    rsDuringDrop,
    supportLevel,
    stop,
    t1,
    t2,
    rr1,
    actionable,
    pullPct,
  };
}
